import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Heading, Text, Input, InputGroup, InputLeftElement, Select,
  SimpleGrid, Button, ButtonGroup, Tag, Card, CardBody, Badge, HStack,
  useColorModeValue, Skeleton, SkeletonText, IconButton, useToast,
} from '@chakra-ui/react';
import { Search, Plus, Library, ChevronLeft, ChevronRight, Download, ExternalLink } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Dashboard/Sidebar';
import { getResources } from '../api/resourceService';
import useResourceOptions from '../hooks/useResourceOptions';
import { useAuth } from '../context/AuthContext';
import { EndorsementCounts, StatusBadge, typeStyle } from '../components/Resources/ResourceBadges';

const REVIEWER_ROLES = ['admin', 'expert', 'super_expert'];
const PAGE_SIZE = 12;


// ============ RESOURCE CARD ============

const ResourceCard = ({ resource, options, showVote }) => {
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const subtextColor = useColorModeValue('gray.500', 'gray.400');
  const { icon: Icon, color } = typeStyle(resource.resource_type);
  const typeLabel = options.resourceType(resource.resource_type)?.label || resource.resource_type;

  return (
    <Card
      variant="outline"
      borderRadius="xl"
      boxShadow="sm"
      borderColor={borderColor}
      bg={cardBg}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{ transform: 'translateY(-4px)', boxShadow: 'lg', borderColor: `${color}.300` }}
      onClick={() => navigate(`/resources/${resource.id}`)}
    >
      <CardBody p={4}>
        <Flex justify="space-between" align="flex-start" mb={3} gap={2}>
          <Flex
            w="40px" h="40px" borderRadius="lg" align="center" justify="center" flexShrink={0}
            bg={`${color}.50`} color={`${color}.500`}
          >
            <Icon size={20} />
          </Flex>
          {resource.status !== 'published' && <StatusBadge status={resource.status} />}
        </Flex>

        <Heading size="sm" noOfLines={2} mb={1}>{resource.title}</Heading>
        <Text fontSize="xs" noOfLines={2} color={subtextColor} mb={3} minH="32px">
          {resource.description}
        </Text>

        <HStack spacing={2} mb={3} flexWrap="wrap">
          <Badge colorScheme={color} variant="subtle" fontSize="2xs">{typeLabel}</Badge>
          <Badge colorScheme="gray" variant="subtle" fontSize="2xs">
            {options.knowledgeLabel(resource.knowledge_type)}
          </Badge>
        </HStack>

        <Flex justify="space-between" align="center">
          <EndorsementCounts resource={resource} />
          <HStack spacing={1} color={subtextColor}>
            {resource.kind === 'file' ? (
              <>
                <Download size={12} />
                <Text fontSize="xs">{resource.download_count || 0}</Text>
              </>
            ) : (
              <ExternalLink size={12} />
            )}
          </HStack>
        </Flex>

        {showVote && resource.my_vote && (
          <Text fontSize="xs" mt={2} color={resource.my_vote === 'endorse' ? 'green.500' : 'red.500'}>
            You voted: {resource.my_vote === 'endorse' ? 'Endorse' : 'Not endorse'}
          </Text>
        )}
      </CardBody>
    </Card>
  );
};

const SkeletonCard = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  return (
    <Card variant="outline" borderRadius="xl" bg={cardBg}>
      <CardBody p={4}>
        <Skeleton h="40px" w="40px" mb={3} borderRadius="lg" />
        <Skeleton h="18px" w="70%" mb={2} />
        <SkeletonText noOfLines={2} spacing={2} mb={3} />
        <Skeleton h="16px" w="50%" />
      </CardBody>
    </Card>
  );
};


// ============ MAIN PAGE ============

const ResourcesPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, loading: authLoading } = useAuth();
  const options = useResourceOptions();
  const isReviewer = REVIEWER_ROLES.includes(user?.role);

  // ?view=review opens the endorsement queue (the sidebar's "Resource Endorsement" link)
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get('view') || 'all'); // 'all' | 'mine' | 'review'
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [resourceType, setResourceType] = useState('all');
  const [knowledgeType, setKnowledgeType] = useState('all');
  const [sort, setSort] = useState('top');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  // A visitor opening ?view=mine, or a non-reviewer opening ?view=review, sees the published list
  const activeView = (!user || (view === 'review' && !isReviewer)) ? 'all' : view;

  const headerBg = useColorModeValue(
    'linear-gradient(135deg, #319795 0%, #2c5282 100%)',
    'linear-gradient(135deg, #234e52 0%, #1a202c 100%)'
  );
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const panelBg = useColorModeValue('white', 'gray.800');
  const pillBg = useColorModeValue('white', 'gray.700');
  const pillColor = useColorModeValue('gray.700', 'gray.200');
  const pillBorderColor = useColorModeValue('gray.200', 'gray.600');

  const fetchResources = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE, sort, view: activeView };
      if (search) params.search = search;
      if (resourceType !== 'all') params.resource_type = resourceType;
      if (knowledgeType !== 'all') params.knowledge_type = knowledgeType;
      // Everyone browsing sees only what experts have published; drafts live under "My submissions"
      if (activeView === 'all') params.status = 'published';

      const data = await getResources(params);
      setResources(data.resources || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch resources:', err);
      toast({ title: 'Failed to load resources', status: 'error', duration: 3000 });
    }
    setLoading(false);
  }, [page, sort, activeView, search, resourceType, knowledgeType, toast]);

  useEffect(() => {
    // Wait until we know who is logged in, so "My submissions" isn't asked for anonymously
    if (!authLoading) fetchResources();
  }, [authLoading, fetchResources]);

  // Follow the URL when the sidebar link is clicked while this page is already open
  useEffect(() => {
    const fromUrl = searchParams.get('view') || 'all';
    setView((current) => (current === fromUrl ? current : fromUrl));
    setPage(1);
  }, [searchParams]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 350));
  };

  const handleViewChange = (nextView) => {
    setView(nextView);
    setPage(1);
    setSearchParams(nextView === 'all' ? {} : { view: nextView });
  };

  const typePills = [{ key: 'all', label: 'All types' }, ...options.resource_types];

  return (
    <Flex direction="row" minH="100vh" bg={pageBg}>
      <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <Box
        pl={sidebarCollapsed ? '60px' : '250px'}
        w="full" overflowY="auto" pb={8}
        transition="padding-left 0.2s ease"
      >
        {/* Header */}
        <Box bg={headerBg} px={8} py={10} color="white" position="relative" overflow="hidden">
          <Box position="absolute" top="-20px" right="-20px" opacity={0.08}>
            <Library size={240} />
          </Box>
          <Flex justify="space-between" align="flex-start" position="relative" zIndex={1} gap={4} wrap="wrap">
            <Box maxW="640px">
              <HStack mb={2}>
                <Library size={28} />
                <Heading size="xl">Resources</Heading>
              </HStack>
              <Text fontSize="md" opacity={0.9} lineHeight="tall">
                Datasets, documents, recitations and links on the Quran, Tafseer, Hadith and more.
                A resource is published once {options.endorsementRule}.
              </Text>
            </Box>
            {user && (
              <Button
                leftIcon={<Plus size={18} />}
                bg="whiteAlpha.200" color="white" size="lg" borderRadius="xl"
                border="1px solid" borderColor="whiteAlpha.300"
                _hover={{ bg: 'whiteAlpha.300' }}
                onClick={() => navigate('/contribute')}
              >
                Share a resource
              </Button>
            )}
          </Flex>
        </Box>

        <Box px={8} mt={-4} position="relative" zIndex={2}>
          {/* Search, knowledge type and sort */}
          <Flex bg={panelBg} borderRadius="xl" boxShadow="lg" p={2} mb={6} gap={2} direction={{ base: 'column', md: 'row' }}>
            <InputGroup size="lg" flex={1}>
              <InputLeftElement pointerEvents="none" h="full">
                <Search color="gray" size={20} />
              </InputLeftElement>
              <Input
                placeholder="Search by title, description or tags..."
                border="none"
                _focus={{ boxShadow: 'none' }}
                onChange={handleSearchChange}
              />
            </InputGroup>
            <Select
              size="lg" maxW={{ md: '260px' }} aria-label="Knowledge type"
              value={knowledgeType}
              onChange={(e) => { setKnowledgeType(e.target.value); setPage(1); }}
            >
              <option value="all">All knowledge types</option>
              {options.knowledge_types.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
            </Select>
            <Select
              size="lg" maxW={{ md: '200px' }} aria-label="Sort"
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(1); }}
            >
              <option value="top">Most endorsed</option>
              <option value="newest">Newest</option>
            </Select>
          </Flex>

          {/* Resource type pills */}
          <Flex mb={6} overflowX="auto" pb={2} gap={2} css={{ '&::-webkit-scrollbar': { display: 'none' } }}>
            {typePills.map((t) => {
              const isActive = resourceType === t.key;
              const color = t.key === 'all' ? 'teal' : typeStyle(t.key).color;
              return (
                <Tag
                  key={t.key}
                  py={2} px={4} borderRadius="full" cursor="pointer" flexShrink={0} whiteSpace="nowrap"
                  bg={isActive ? `${color}.500` : pillBg}
                  color={isActive ? 'white' : pillColor}
                  border="1px solid"
                  borderColor={isActive ? 'transparent' : pillBorderColor}
                  fontWeight="medium" fontSize="sm"
                  onClick={() => { setResourceType(t.key); setPage(1); }}
                >
                  {t.label}
                </Tag>
              );
            })}
          </Flex>

          {/* Results header */}
          <Flex justify="space-between" align="center" mb={4} gap={3} wrap="wrap">
            <Text fontSize="sm" color="gray.500" fontWeight="medium">
              {loading ? 'Loading...' : `${total} resource${total !== 1 ? 's' : ''}`}
            </Text>
            {user && (
              <ButtonGroup size="sm" isAttached variant="outline">
                <Button onClick={() => handleViewChange('all')} isActive={activeView === 'all'}>Published</Button>
                <Button onClick={() => handleViewChange('mine')} isActive={activeView === 'mine'}>My submissions</Button>
                {isReviewer && (
                  <Button onClick={() => handleViewChange('review')} isActive={activeView === 'review'}>
                    Awaiting endorsement
                  </Button>
                )}
              </ButtonGroup>
            )}
          </Flex>

          {loading ? (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={5}>
              {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
            </SimpleGrid>
          ) : resources.length === 0 ? (
            <Flex direction="column" align="center" justify="center" py={20}>
              <Box opacity={0.3} mb={4}><Library size={80} /></Box>
              <Heading size="md" mb={2} color="gray.500">No resources found</Heading>
              <Text color="gray.400" mb={6} textAlign="center">
                {activeView === 'review'
                  ? 'Nothing is waiting for your endorsement right now.'
                  : 'Try another search or filter, or share the first one.'}
              </Text>
              {user && (
                <Button leftIcon={<Plus size={16} />} colorScheme="teal" onClick={() => navigate('/contribute')}>
                  Share a resource
                </Button>
              )}
            </Flex>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={5}>
              {resources.map((r) => (
                <ResourceCard key={r.id} resource={r} options={options} showVote={isReviewer} />
              ))}
            </SimpleGrid>
          )}

          {totalPages > 1 && (
            <Flex justify="center" align="center" mt={8} gap={3}>
              <IconButton
                aria-label="Previous page" icon={<ChevronLeft size={18} />}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                isDisabled={page === 1} variant="outline" borderRadius="full" size="sm"
              />
              <Text fontSize="sm">Page {page} of {totalPages}</Text>
              <IconButton
                aria-label="Next page" icon={<ChevronRight size={18} />}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                isDisabled={page === totalPages} variant="outline" borderRadius="full" size="sm"
              />
            </Flex>
          )}
        </Box>
      </Box>
    </Flex>
  );
};

export default ResourcesPage;
